/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useEffect} from "react";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import BookingAction, {PackageCompetition} from "store/booking/BookingAction";
import {PackageItemCompetition} from "types/entities/PackageItem";
import {getSelectedCompetition} from "utils/utility";
import {isCompetitionInGroups, useCompetitions} from "utils/hooks/useCompetitions";
import {PackageCategory} from "types/enums/PackageCategory";
import {CreditItem} from "types/entities/PackagesItems";

export interface CompetitionSelection {
    isFreeSelect: boolean;
    isFree: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    count: number;
    needsCredit: boolean;
    personsWithCredits: number;
    hasEnoughCredits: boolean;
    hasUnmetDependency: boolean;
    totalSelectedCreditsValue: number;
    totalSelectedCreditsQuantity: number;
    isFamilyCategory: boolean;
    toggleCompetition: () => void;
    setCount: (count: number) => void;
    hasDepends: boolean;
    maxCount: number;
}

// how many persons of the booking have a credit item selected
export const usePersonsWithCredits = (): number => {
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);
    return selectedPackageCreditItems.filter(creditItem => creditItem.id !== null && creditItem.quantity > 0).length;
};

export const useSelectedCreditsValue = () => {
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);
    const availableCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.availableCreditItems);

    return selectedPackageCreditItems.reduce((acc, selectedItem) => {
        const creditItem = availableCreditItems.find((item: CreditItem) => item.id === selectedItem.id);
        const value = creditItem?.article?.article_value || 0;
        return acc + value * selectedItem.quantity;
    }, 0);
};

export const useSelectedCreditsQuantity = () => {
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);
    return selectedPackageCreditItems.reduce((acc, item) => acc + item.quantity, 0);
};

// selection of a single competition item, kept apart from the upsell selection
export const useCompetitionSelection = (packageItem: PackageItemCompetition): CompetitionSelection => {
    const dispatch = useDispatch();
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.competition);
    const personsWithCredits = usePersonsWithCredits();
    const totalSelectedCreditsValue = useSelectedCreditsValue();
    const totalSelectedCreditsQuantity = useSelectedCreditsQuantity();
    const {
        selectedFreeCompetitions,
        freeCompetitionsCount,
        freeSelectCompetitions,
        selectedFreeCompetitionsCount,
        renounceFreeCompetitions,
        removeNewlyAddedPaidCompetitions,
    } = useCompetitions();

    const categoriesWithFamily = [PackageCategory.Action, PackageCategory.Kinder];

    const isFamilyCategory = bookingFilter.category ? categoriesWithFamily.includes(bookingFilter.category) : false;

    const persons = +bookingFilter.adults + +bookingFilter.children;
    const isFree = isCompetitionInGroups(freeSelectCompetitions, packageItem);
    const isFreeSelect = freeCompetitionsCount > 0 && isFree;
    const selectedCompetition = getSelectedCompetition(selectedCompetitions, packageItem);
    const isSelected = selectedCompetition !== undefined;

    const count = selectedCompetition?.count || 0;
    const maxCount = packageItem.packageItemVariant?.maximum_count || 0;

    const needsCredit = packageItem.competition?.needs_credit;
    const hasEnoughCredits =
        !needsCredit ||
        (isFamilyCategory
            ? totalSelectedCreditsValue >= (packageItem.competition?.needs_credit_amount || 0)
            : totalSelectedCreditsQuantity >= packageItem.minimum_persons_count);

    // options can require another competition of the same group to be selected first
    const dependsOnArticleIds = packageItem.extra.depends_on_article_id ?? [];
    const hasDepends = dependsOnArticleIds.length > 0;
    const hasUnmetDependency =
        dependsOnArticleIds.length > 0 && !selectedCompetitions.some(competition => dependsOnArticleIds.includes(competition.packageItem.article_id));

    const isDisabled = !hasEnoughCredits || (!isSelected && hasUnmetDependency);

    const setCount = (newCount: number) => {
        if (!selectedCompetition) return;
        if (newCount <= 0) {
            const updatedCompetitions = selectedCompetitions.filter(c => c.packageItem.id !== selectedCompetition.packageItem.id);
            dispatch(BookingAction.removeCompetition(selectedCompetition));

            if (isFreeSelect) {
                const willBeComplete = updatedCompetitions.filter(c => c.is_free && c.packageItem.id !== 0).length >= freeCompetitionsCount || renounceFreeCompetitions;
                if (!willBeComplete) {
                    removeNewlyAddedPaidCompetitions(updatedCompetitions);
                }
            }
            return;
        }
        dispatch(BookingAction.setCompetition({packageItem, upsell_option: 0, count: newCount, is_free: isFreeSelect}));
    };

    const removeFreeSelectionsOverLimit = () => {
        const overLimit = selectedFreeCompetitions.length - freeCompetitionsCount + 1;
        if (overLimit <= 0) return;

        const currentSelectedCompetitions = selectedFreeCompetitions.filter(competition => competition.packageItem.id !== packageItem.id);

        currentSelectedCompetitions.slice(0, overLimit).forEach((competition: PackageCompetition) => {
            dispatch(BookingAction.removeCompetition(competition));
        });
    };

    const toggleCompetition = () => {
        const renounceItem = selectedCompetitions.find(c => c.is_free && c.packageItem.id === 0);
        if (renounceItem) {
            dispatch(BookingAction.removeCompetition(renounceItem));
        }

        if (isSelected && selectedCompetition) {
            const updatedCompetitions = selectedCompetitions.filter(c => c.packageItem.id !== selectedCompetition.packageItem.id);
            dispatch(BookingAction.removeCompetition(selectedCompetition));

            if (isFreeSelect) {
                const willBeComplete = updatedCompetitions.filter(c => c.is_free && c.packageItem.id !== 0).length >= freeCompetitionsCount || renounceFreeCompetitions;
                if (!willBeComplete) {
                    removeNewlyAddedPaidCompetitions(updatedCompetitions);
                }
            }
            return;
        }
        if (isDisabled) return;

        // picking another included competition replaces the one selected before
        if (isFreeSelect) {
            removeFreeSelectionsOverLimit();

            if (selectedFreeCompetitionsCount + 1 >= freeCompetitionsCount) {
                dispatch(BookingAction.setRenounceFreeCompetitions(false));
            }
        }
        dispatch(BookingAction.pushCompetition({packageItem, upsell_option: 0, count: 1, is_free: isFreeSelect}));
    };

    // drop the selection as soon as the competition it depends on is gone
    useEffect(() => {
        if (isSelected && selectedCompetition && hasUnmetDependency) {
            dispatch(BookingAction.removeCompetition(selectedCompetition));
        }
    }, [isSelected, hasUnmetDependency]);

    return {
        isFreeSelect,
        isFree,
        isSelected,
        isDisabled,
        count,
        needsCredit,
        personsWithCredits,
        hasEnoughCredits,
        hasUnmetDependency,
        hasDepends,
        totalSelectedCreditsValue,
        totalSelectedCreditsQuantity,
        isFamilyCategory,
        toggleCompetition,
        setCount,
        maxCount,
    };
};
